import { useEffect, useState } from 'react'
import { TableOfContents, type TocItem } from './TableOfContents'
import { Footer } from './Footer'
import { Navigation } from './Navigation'

interface LegalPageLayoutProps {
  title: string
  lastUpdated: string
  tocItems: TocItem[]
  children: React.ReactNode
}

export const LegalPageLayout = ({
  title,
  lastUpdated,
  tocItems,
  children,
}: LegalPageLayoutProps) => {
  const [activeSection, setActiveSection] = useState<string>('')

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = tocItems.map((item) => document.getElementById(item.id))
      const scrollPosition = window.scrollY + 120

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tocItems[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [tocItems])

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      const offset = 100
      const elementPosition = element.offsetTop - offset
      window.scrollTo({ top: elementPosition, behavior: 'smooth' })
    }
  }

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />

      <header className="pt-28 sm:pt-32 pb-8 sm:pb-12 bg-gradient-to-b from-neutral-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight">
            {title}
          </h1>
          <p className="mt-3 text-sm sm:text-base text-neutral-500">
            Last updated: {lastUpdated}
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        <div className="flex items-start gap-12">
          <aside className="hidden lg:block w-56 shrink-0 sticky top-24 h-fit">
            <TableOfContents
              items={tocItems}
              activeSection={activeSection}
              onItemClick={scrollToSection}
            />
          </aside>

          <article className="flex-1 min-w-0 max-w-3xl">
            <div className="prose prose-neutral max-w-none prose-headings:scroll-mt-28 prose-h2:text-xl prose-h2:font-semibold prose-h2:text-neutral-900 prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-lg prose-h3:font-medium prose-h3:text-neutral-800 prose-p:text-neutral-600 prose-p:leading-relaxed prose-a:text-orange-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-neutral-900 prose-li:text-neutral-600">
              {children}
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  )
}
